import React from 'react';
import { FormattedMessage } from 'react-intl';
import { intl } from '../../../locales/localesConfig';
import InterfaceTooling from '../../../tooling/interfaceTooling';
import JavascriptUtilsTooling from '../../../tooling/javascriptUtilsTooling';
import FormatTooling from '../../../tooling/formatTooling';
import LocaleTooling from '../../../tooling/localeTooling';
import Constants from '../../../definitions/constants';

import GlassPopup from '../common/interface/glassPopup';
import ToggleSection from '../common/interface/toggleSection';
import TextBox from '../common/inputs/textBox';
import CheckBox from '../common/inputs/checkBox';
import Select from '../common/inputs/select';
import SelectList from '../common/inputs/selectList';
import ObjectDefinitionEditor from '../common/inputs/objectDefinitionEditor';
import ObjectMetricVirtualMarksLabelEditor from '../common/inputs/objectMetricVirtualMarksLabelEditor';
import Label from '../common/inputs/label';
import ErrorFeedback from '../common/feedback/errorFeedback';
import VersionsTable from '../common/versions/versionsTable';
import TagsSelector from '../common/inputs/tagsSelector';

const FormPopup = props => {
  const {
    handleShowPopup,
    handleOnChangeForm,
    handleButton,
    loadingButton,
    fullLoading,
    statusButton,
    formData,
    dimensionsList,
    errors,
    entityNameSingular,
    popupState,
    handleShowMetricVersionPopup,
    handleEntityQueryDataPopup,
    tagsOptions,
  } = props;

  return (
    <GlassPopup
      className="form_popup"
      title={(<FormattedMessage 
         id={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? "common.entity.editEntity" : "common.entity.createEntity"}
         values={{entityNameSingular: entityNameSingular}}
       />)}
      buttonText={(<FormattedMessage 
         id={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? "common.entity.editEntity" : "common.entity.createEntity"}
         values={{entityNameSingular: entityNameSingular}}
       />)}
      type={Constants.interface.popup.types.LATERAL}
      {...props}
    >
      <ErrorFeedback
        status={statusButton}
        fieldErrors={errors}
        localesFieldsPath="metricAdmin.fields"
        optionalStatusErrors={{
          "406": intl.formatMessage({id: "common.entity.saveEntityCantDeactivateError"},
             {entityNameSingular: FormatTooling.toUpperCaseFirst(intl.formatMessage({id: "metricAdmin.entityNameSingularWithArticle"})), 
              detail: (errors.message ? intl.formatMessage({id: "common.errors.detailLabel"}) + errors.message : "")
             })
        }}
      />
      {JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") && (
        <i 
          className="fas fa-search entity_use_query_icon" 
          data-entityid={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id")} 
          onClick={handleEntityQueryDataPopup}
        />
      )}
      <TextBox 
        styleType={Constants.interface.inputs.styleType.OUTLINED}
        type={Constants.interface.inputs.type.TEXT}
        id="name"
        data-path="metric.name"
        value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.name")}
        onChange={handleOnChangeForm}
        labelLocale={<FormattedMessage id="metricAdmin.fields.name"/>}
        className={("name" in errors) ? "has_error" : null}
      />
      <CheckBox 
        id="unique"
        data-path="metric.unique"
        value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.unique")}
        onChange={handleOnChangeForm}
        disabled={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? true : false}
        labelLocale={<FormattedMessage id="metricAdmin.fields.unique"/>}
      />
      <Select 
        id="metric_type"
        data-path="metric.metric_type"
        value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.metric_type")}
        onChange={handleOnChangeForm}
        labelLocale={<FormattedMessage id="metricAdmin.fields.metric_type"/>}
        disabled={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? true : false}
        options={InterfaceTooling.getSelectArray("selectKeys.metric.metricType","metricAdmin.fields.selectValues.metric_type", false)}
        className={("metric_type" in errors) ? "has_error" : null}
      />
      <Select 
        id="data_type"
        data-path="metric.data_type"
        value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.data_type")}
        onChange={handleOnChangeForm}
        labelLocale={<FormattedMessage id="metricAdmin.fields.data_type"/>}
        disabled={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? true : false}
        options={InterfaceTooling.getSelectArray("selectKeys.metric.dataType","metricAdmin.fields.selectValues.data_type")}
        className={("data_type" in errors) ? "has_error" : null}
      />
      {JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.data_type")==Constants.selectKeys.metric.dataType.DIMENSION && (
        <Select 
          id="validation_dimension_id"
          data-path="metric.validation_dimension_id"
          value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.validation_dimension_id")}
          onChange={handleOnChangeForm}
          labelLocale={<FormattedMessage id="metricAdmin.fields.validation_dimension_id"/>}
          disabled={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? true : false}
          options={InterfaceTooling.getSelectArrayFromArrayOfObjects(dimensionsList.filter(item => !item.system_entity_class), "id", "name")}
          className={("validation_dimension" in errors) ? "has_error" : null}
        />
      )}
      {JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.data_type")==Constants.selectKeys.metric.dataType.OBJECT && (
        <ObjectDefinitionEditor 
          id="object_definition"
          data-path="metric.object_definition"
          values={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.object_definition")}
          onChange={handleOnChangeForm}
          onlyEnabledAdd={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? true : false}
          dataTypeOptions={InterfaceTooling.getSelectArray("selectKeys.metric.objectDefinitionDataType","metricAdmin.fields.selectValues.data_type")}
          validationDimensionsOptions={InterfaceTooling.getSelectArrayFromArrayOfObjects(dimensionsList.filter(item => !item.system_entity_class), "id", "name")}
          className={("object_definition" in errors) ? "has_error" : null}
        />
      )}
      <SelectList        
        values={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.dimensions")}
        onChange={handleOnChangeForm}
        data-path="metric.dimensions"
        addSelectLocale={<FormattedMessage id="metricAdmin.addDimensionValue"/>}
        labelLocale={<FormattedMessage id="metricAdmin.fields.dimensions"/>}
        noMoreOptionsLocale={<FormattedMessage id="metricAdmin.noMoreOptions"/>}
        options={InterfaceTooling.getSelectArrayFromArrayOfObjects(dimensionsList, "id", "name", true, intl.formatMessage({id: "metricAdmin.addDimensionValue"}))}
        valueAccesor={"id"}
        labelAccesor={"name"}
        disabled={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") ? true : false}
        className={("dimensions" in errors) ? "has_error" : null}
      />
      <div className="row_fields">
        <CheckBox 
          id="allow_unknown_calculation_unit_dimension"
          data-path="metric.allow_unknown_calculation_unit_dimension"
          value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.allow_unknown_calculation_unit_dimension")}
          onChange={handleOnChangeForm}
          labelLocale={LocaleTooling.getCalculationUnitContextLocale(<FormattedMessage id="metricAdmin.fields.allow_unknown_calculation_unit_dimension"/>)}
          className={("allow_unknown_calculation_unit_dimension" in errors) ? "has_error" : null}
        />
      </div>
      <CheckBox 
        className="display_block_checkbox"
        id="exclude_from_auto_groups"
        data-path="metric.exclude_from_auto_groups"
        value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.exclude_from_auto_groups")}
        onChange={handleOnChangeForm}
        labelLocale={<FormattedMessage id="metricAdmin.fields.exclude_from_auto_groups"/>}
      />
      <div className="row_fields">
        <CheckBox 
          id="exclude_from_system_dimension_metrics"
          data-path="metric.exclude_from_system_dimension_metrics"
          value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.exclude_from_system_dimension_metrics")}
          onChange={handleOnChangeForm}
          labelLocale={<FormattedMessage id="metricAdmin.fields.exclude_from_system_dimension_metrics"/>}
        />
        {JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.metric_type")==Constants.selectKeys.metric.metricType.DYNAMIC && (
          <CheckBox 
            id="dynamic_metric_force_calculation_cache"
            data-path="metric.dynamic_metric_force_calculation_cache"
            value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.dynamic_metric_force_calculation_cache")}
            onChange={handleOnChangeForm}
            labelLocale={<FormattedMessage id="metricAdmin.fields.dynamic_metric_force_calculation_cache"/>}
          />
        )}
      </div>
      {JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") && (
        <div className="row_fields">
          <CheckBox 
            id="active"
            data-path="metric.active"
            value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.active")}
            onChange={handleOnChangeForm}
            labelLocale={<FormattedMessage id="common.entity.fields.active"/>}
          />
        </div>
      )}
      <ToggleSection title={<FormattedMessage id="metricAdmin.fields.reportsConfigTitle"/>}>
        <div>
          <TextBox 
            styleType={Constants.interface.inputs.styleType.OUTLINED}
            type={Constants.interface.inputs.type.TEXT}
            id="report_value_label"
            data-path="metric.report_value_label"
            value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.report_value_label")}
            onChange={handleOnChangeForm}
            labelLocale={<FormattedMessage id="metricAdmin.fields.report_value_label"/>}
            className={("report_value_label" in errors) ? "has_error" : null}
          />
          <TextBox 
            styleType={Constants.interface.inputs.styleType.OUTLINED}
            type={Constants.interface.inputs.type.TEXT}
            id="format"
            data-path="metric.format"
            value={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.format")}
            onChange={handleOnChangeForm}
            labelLocale={<FormattedMessage id="metricAdmin.fields.format"/>}
            className={("format" in errors) ? "has_error" : null}
          />
          <ObjectMetricVirtualMarksLabelEditor 
            id="virtual_marks_labels"
            data-path="metric.virtual_marks_labels"
            values={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.virtual_marks_labels")}
            onChange={handleOnChangeForm}
            className={("virtual_marks_labels" in errors) ? "has_error" : null}
          />
        </div>
      </ToggleSection>
      {!JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id") && (
        <label className="form_info">
          <FormattedMessage 
            id={"metricAdmin.preCreateInfo"}
          />
        </label>
      )}
      {(false && JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.id")) && (
        <VersionsTable
          versions={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.metric_versions")}
          onClickVersion={handleShowMetricVersionPopup}
        />
      )}
      <TagsSelector
        id={"tags"}
        values={JavascriptUtilsTooling.getObjectStringPathKeyValue(formData,"metric.tags")}
        onClickOption={handleOnChangeForm}
        options={tagsOptions}
        data-path="metric.tags"
      />
    </GlassPopup>
  );
};

export default FormPopup;



// WEBPACK FOOTER //
// ./source/client/components/metricAdmin/formPopup.jsx